import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from "react-dom"
import styles from './TeamGrid.module.css';
import Arrow from '../assets/arrowTopRight.jsx';
import Close from '../assets/Close.jsx';
import Mark from '../assets/Mark.jsx';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import toSentenceCase from '../utilities/sentenceCase.js';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { useParams } from "react-router-dom";

function TeamGrid({ title, teamCategories = [] }) {

    // get the current lang for routing
    const { lang } = useParams();

    // Quick View
    const quickViewBioEl = useRef();
    const quickViewSubHeadline = useRef();
    const quickViewHeadline = useRef();
    const quickViewMediaImg = useRef();
    const quickViewSummary = useRef();
    const quickViewSummaryDetails = useRef();

    const handleKeyDown = (event) => {
        if (event.key === 'Escape' || event.key === 'Esc') {
            closeQuickView();
        }
    };

    const handleClickOutside = (event) => {
      // If the panel is open and click is outside the panel, close it
      if (quickViewBioEl.current && !quickViewBioEl.current.contains(event.target)) {
        closeQuickView();
      }
        
    }

    let quickViewOpen = false;

    const openQuickView = (item) => {

        // make sure quick view isn't already open
        if (quickViewOpen) return;

        document.addEventListener('keydown', handleKeyDown);

        document.querySelector("body").classList.add("overlay");
        quickViewBioEl.current.classList.add("active");

        // setup the item variables
        const subheadline = item.fields.jobTitle ? item.fields.jobTitle : '';
        const headline = item.fields.teamMemberName ? item.fields.teamMemberName : '';
        const summary = item.fields.bioText ? documentToHtmlString(item.fields.bioText) : null;
        const imageUrl = item.fields?.image?.fields?.file?.url;
        const projectImg = imageUrl ? `https:${imageUrl}` : null;

        // inject into quick view player
        quickViewSubHeadline.current.innerHTML = subheadline;
        quickViewHeadline.current.innerHTML = headline;

        if (summary != null) {
            quickViewSummary.current.style.display = 'flex';
            quickViewSummaryDetails.current.innerHTML = summary;
        }

        if (projectImg != null) {
            quickViewMediaImg.current.src = projectImg;
            quickViewMediaImg.current.style.display = 'block';
        } else {
            quickViewSummary.current.classList.add("no_media");
        }

        // add a click outside function to close the quick view
       document.addEventListener('mousedown', handleClickOutside);

       quickViewOpen = true;
    }

    const closeQuickView = () => {

        document.removeEventListener('keydown', handleKeyDown);

        document.querySelector("body").classList.remove("overlay");
        quickViewBioEl.current.classList.remove("active");

        setTimeout(function() {
            quickViewMediaImg.current.style.display = 'none';
            quickViewSummary.current.style.display = 'none';
            quickViewSummary.current.classList.remove("no_media");
            document.removeEventListener('mousedown', handleClickOutside);
        }, 600);

        setTimeout(function() {

            quickViewOpen = false;

        }, 650);

    }

    function QuickViewBox() {

        return ReactDOM.createPortal(
            <div id="quickview" onClick={() => closeQuickView()} className="bio" ref={quickViewBioEl}>
                <div className="header">
                    <div className="close">
                        <Close color="#fafafa" /> {lang === "ko" ? "닫기" : "CLOSE"}
                    </div>
                </div>
                <div className="body">
                    
                    <h4 className="subheadline" ref={quickViewSubHeadline}></h4>
                    <h2 className="headline" ref={quickViewHeadline}></h2>

                    <div className="summary" ref={quickViewSummary}>

                        <div className="media">
                            <img src="" className="photo" ref={quickViewMediaImg} />
                        </div>

                        {/*
                        <div className="title">ABOUT</div>
                        */}
                        <div className="details preserve-line-breaks" ref={quickViewSummaryDetails}>

                        </div>
                    </div>
                </div>
            </div>,
            document.body
        );
    }

    const [selectedCategoryId, setSelectedCategoryId] = useState(null);

    // measure grid/container width so we can compute proportional widths that flush
    const gridRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);

    // config (keep numbers in JS for math; CSS uses matching values)
    const itemsPerRow = 3;
    const minRowHeight = 80; // px fallback / minimum

    const colGap = 20; // px, must match CSS gap

    useEffect(() => {
        setSelectedCategoryId(teamCategories?.[0]?.sys?.id ?? null);
    }, [teamCategories]);

    useEffect(() => {
        if (!gridRef.current) return;
        const ro = new ResizeObserver(() => {
            if (typeof gridRef.current != "undefined" && gridRef.current != null) {
                setContainerWidth(Math.floor(gridRef.current.clientWidth));
            }
        });
        ro.observe(gridRef.current);
        setContainerWidth(Math.floor(gridRef.current.clientWidth));
        return () => ro.disconnect();
    }, []);

    const handleSelect = (id) => setSelectedCategoryId(id);

    const getIntrinsicRatio = (member) => {
        const w = member?.fields?.image?.fields?.file?.details?.image?.width ?? null;
        const h = member?.fields?.image?.fields?.file?.details?.image?.height ?? null;
        if (w && h) return w / h;
        return 1;
    };

    const safeImgUrl = (member) => {
        const raw = member?.fields?.image?.fields?.file?.url ?? null;
        if (!raw) return null;
        return raw.startsWith('//') ? `https:${raw}` : raw;
    };

    return (
        <div className={styles.team_grid} id="team" ref={gridRef}>
            <div className={styles.section_title}>
                <h1>{title}</h1>
                <div className={styles.filter}>
                    <ul>
                        {teamCategories.map((category) => {
                            const id = category?.sys?.id;
                            const label = category?.fields?.title ?? 'Untitled';

                            return (
                                <li key={id}>
                                    <button
                                        type="button"
                                        onClick={() => handleSelect(id)}
                                        aria-pressed={selectedCategoryId === id}
                                    >
                                        {label}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>

            <div className={styles.grid}>
                {teamCategories.map((category) => {
                    if (selectedCategoryId && category?.sys?.id !== selectedCategoryId) return null;

                    const members = category?.fields?.teamMembers ?? [];

                    // group into rows
                    const rows = [];
                    for (let i = 0; i < members.length; i += itemsPerRow) {
                        rows.push(members.slice(i, i + itemsPerRow));
                    }

                    // compute normalRowHeight from the first full row (so short last rows don't stretch)
                    let normalRowHeight = null;
                    if (containerWidth && rows.length) {
                        const fullRow = rows.find((r) => r.length === itemsPerRow) || rows[0];
                        const fullRatios = fullRow.map(getIntrinsicRatio);
                        const fullRatioSum = fullRatios.reduce((s, r) => s + r, 0) || 1;
                        const fullAvailableWidth = Math.max(0, containerWidth - (itemsPerRow - 1) * colGap);
                        normalRowHeight = Math.max(minRowHeight, Math.floor(fullAvailableWidth / fullRatioSum));
                    }

                    return (
                        <section
                            className={styles.category}
                            key={category.sys.id}
                            aria-labelledby={`cat-${category.sys.id}`}
                        >
                            <div className={styles.categoryGrid}>
                                {rows.map((row, rowIndex) => {
                                    const ratios = row.map(getIntrinsicRatio);
                                    const ratioSum = ratios.reduce((s, r) => s + r, 0) || 1;

                                    // available width for this row (subtract gaps between columns)
                                    const availableWidth = containerWidth
                                        ? Math.max(0, containerWidth - (row.length - 1) * colGap)
                                        : 0;

                                    // full rows fill container; short rows use normalRowHeight (left aligned)
                                    const heightPx =
                                        row.length === itemsPerRow
                                            ? containerWidth
                                                ? Math.max(minRowHeight, Math.floor(availableWidth / ratioSum))
                                                : 240
                                            : normalRowHeight ?? (availableWidth ? Math.max(minRowHeight, Math.floor(availableWidth / ratioSum)) : 240);

                                    return (
                                        <div
                                            className={`${styles.row} ${row.length === itemsPerRow ? styles.fullRow : styles.shortRow}`}
                                            key={`${rowIndex}_${category.sys.id}`}
                                            // only setting CSS variables for dynamic sizes
                                            style={{ '--row-height': `${heightPx}px` }}
                                        >
                                            {row.map((member, i) => {
                                                const id = member?.sys?.id ?? `${rowIndex}-${i}`;
                                                const ratio = ratios[i] || 1;
                                                const widthPx = Math.max(40, Math.round(ratio * heightPx));
                                                const imgUrl = safeImgUrl(member);

                                                return (
                                                    <div
                                                        className={styles.cell}
                                                        key={`${id}_${i}`}
                                                        style={{ '--cell-width': `${widthPx}px` }}
                                                    >
                                                        
                                                        <div className={styles.mediaWrap}>
                                                            <a onClick={(e) => {e.preventDefault(); openQuickView(member)}} href={`/${lang}/about/#${member?.fields?.teamMemberName ?? ''}`}>
                                                                {imgUrl ? (
                                                                    <img
                                                                        src={imgUrl}
                                                                        alt={member?.fields?.teamMemberName ?? ''}
                                                                        className={styles.mediaImg}
                                                                    />
                                                                ) : (
                                                                    <div className={styles.emptyMedia}>
                                                                        <div className={styles.mark}>
                                                                            <Mark />
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </a>
                                                            <div className={styles.cta}>
                                                                {lang === "ko" ? "이력 보기" : "READ BIO"}
                                                            </div>
                                                        </div>

                                                        <div className={styles.info}>
                                                            <h4 className={styles.name}>{member?.fields?.teamMemberName}</h4>
                                                            <p className={styles.role}>{member?.fields?.jobTitle}</p>
                                                        </div>
                                                        
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    );
                })}
            </div>

            <QuickViewBox />

        </div>
    );
}

export default TeamGrid;